import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectGameTags } from '../../store/selectors/gamesSelectors';
import { fetchGameTags } from '../../store/thunks/gamesThunks';

import './GameTagFilter.css';

type GameTagFilterProps = {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
};

export function GameTagFilter({ selectedTags, onTagsChange }: GameTagFilterProps) {
  const dispatch = useAppDispatch();
  const allTags = useAppSelector(selectGameTags);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allTagsExpanded, setAllTagsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (allTags.length === 0) {
      dispatch(fetchGameTags());
    }
  }, [dispatch, allTags.length]);

  const allTagNames = allTags.map((t) => t.name);
  const availableTags = allTagNames.filter((name) => !selectedTags.includes(name));

  const suggestions = inputValue.trim()
    ? availableTags.filter((name) =>
        name.toLowerCase().includes(inputValue.toLowerCase().trim()),
      )
    : availableTags;

  const addTag = useCallback(
    (tag: string) => {
      const normalized = tag.toLowerCase().trim();
      if (normalized && !selectedTags.includes(normalized)) {
        onTagsChange([...selectedTags, normalized]);
      }
      setInputValue('');
      setShowSuggestions(false);
    },
    [selectedTags, onTagsChange],
  );

  const removeTag = useCallback(
    (tag: string) => {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    },
    [selectedTags, onTagsChange],
  );

  return (
    <div className="tag-filter">
      {selectedTags.length > 0 && (
        <div className="tag-filter__selected">
          {selectedTags.map((tag) => (
            <span key={tag} className="tag-filter__chip">
              {tag}
              <button
                className="tag-filter__chip-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Убрать фильтр ${tag}`}
                type="button"
              >
                ×
              </button>
            </span>
          ))}
          <button
            className="tag-filter__clear-all"
            onClick={() => onTagsChange([])}
            type="button"
          >
            Сбросить все
          </button>
        </div>
      )}

      <div className="tag-filter__field-wrap">
        <input
          ref={inputRef}
          className="tag-filter__field"
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Фильтр по тегам..."
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="tag-filter__suggestions">
            {suggestions.slice(0, 10).map((name) => (
              <button
                key={name}
                className="tag-filter__suggestion"
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(name);
                }}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {availableTags.length > 0 && (
        <div className="tag-filter__all-tags">
          <button
            className="tag-filter__toggle-btn"
            onClick={() => setAllTagsExpanded((prev) => !prev)}
            type="button"
          >
            <span className={`tag-filter__toggle-icon${allTagsExpanded ? ' tag-filter__toggle-icon--open' : ''}`}>
              ▸
            </span>
            Все теги ({availableTags.length})
          </button>

          {allTagsExpanded && (
            <div className="tag-filter__all-tags-list">
              {availableTags.map((name) => (
                <button
                  key={name}
                  className="tag-filter__pickable-tag"
                  onClick={() => addTag(name)}
                  type="button"
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
